/**
 * useChatScroll — owns the chat-scroll hook cluster as ONE unit (hook call order
 * inside the block must stay fixed; the dependency arrays are load-bearing).
 *
 * The scroll refs (`messagesContainerRef`, `userHasScrolledRef`,
 * `isInitialLoadRef`, `forceBottomRef`) are passed in, never recreated here —
 * the conversation-change reset effect and the send path read the SAME refs.
 * `hardScrollToBottom` is internal to the cluster, so it is NOT returned.
 */
import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';

interface ForceBottomState {
  conversationId: string | null;
  active: boolean;
  startedAtMs: number;
}

interface UseChatScrollArgs {
  messagesContainerRef: React.RefObject<HTMLDivElement | null>;
  userHasScrolledRef: React.MutableRefObject<boolean>;
  isInitialLoadRef: React.MutableRefObject<boolean>;
  forceBottomRef: React.MutableRefObject<ForceBottomState>;
  activeConversation: { id: string } | null;
  isStreaming: boolean;
  isLoading: boolean;
  messages: unknown[];
  setIsScrollSwitchSettling: React.Dispatch<React.SetStateAction<boolean>>;
  /** During onboarding the beat transcript owns scrolling (smooth scroll-to-top
   *  of each new beat in MessageList). Suppress the instant snap-to-bottom here so
   *  the two don't fight and the flow doesn't "click into place". */
  onboardingActive?: boolean;
}

export function useChatScroll({
  messagesContainerRef,
  userHasScrolledRef,
  isInitialLoadRef,
  forceBottomRef,
  activeConversation,
  isStreaming,
  isLoading,
  messages,
  setIsScrollSwitchSettling,
  onboardingActive = false,
}: UseChatScrollArgs): void {
  const onboardingActiveRef = useRef(onboardingActive);
  onboardingActiveRef.current = onboardingActive;
  const hardScrollToBottom = useCallback(() => {
    // During onboarding the beat transcript scrolls itself smoothly (MessageList
    // scrolls each new beat's top into view). Suppress the instant jump-to-bottom
    // so the two don't collide and the flow doesn't snap/"click into place".
    if (onboardingActiveRef.current) return;
    const container = messagesContainerRef.current;
    if (!container) return;
    container.scrollTop = container.scrollHeight;
  }, []);

  // On conversation switch, force the message scroller to bottom before paint.
  useLayoutEffect(() => {
    const conversationId = activeConversation?.id ?? null;
    if (!conversationId) return;

    userHasScrolledRef.current = false;
    isInitialLoadRef.current = true;
    forceBottomRef.current = {
      conversationId,
      active: true,
      startedAtMs: performance.now(),
    };
    hardScrollToBottom();
  }, [activeConversation?.id, hardScrollToBottom]);

  // Track whether user has scrolled up (away from bottom)
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const onScroll = () => {
      const forceBottom = forceBottomRef.current;
      if (forceBottom.active && forceBottom.conversationId === (activeConversation?.id ?? null)) {
        userHasScrolledRef.current = false;
        return;
      }

      const { scrollTop, scrollHeight, clientHeight } = container;
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;

      // During streaming, use a larger threshold so markdown reflows don't
      // accidentally mark the user as "scrolled away"
      const threshold = isStreaming ? 150 : 50;

      if (distanceFromBottom > threshold) {
        userHasScrolledRef.current = true;
      }
      if (distanceFromBottom < 10) {
        userHasScrolledRef.current = false;
      }

    };
    container.addEventListener('scroll', onScroll, { passive: true });
    return () => container.removeEventListener('scroll', onScroll);
  }, [activeConversation?.id, isStreaming]);

  // Keep chat pinned to bottom unless user intentionally scrolls away.
  useLayoutEffect(() => {
    const conversationId = activeConversation?.id ?? null;
    const container = messagesContainerRef.current;
    if (!conversationId || !container) return;

    const forceBottom = forceBottomRef.current;
    const shouldForceBottom = forceBottom.active && forceBottom.conversationId === conversationId;

    if (shouldForceBottom) {
      if (!userHasScrolledRef.current) {
        hardScrollToBottom();
      }

      const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
      const ageMs = performance.now() - forceBottom.startedAtMs;
      const hasSettled = !isLoading && distanceFromBottom <= 2 && ageMs >= 200;
      const hasTimedOut = ageMs >= 1200;
      if (hasSettled || hasTimedOut) {
        forceBottomRef.current = { ...forceBottom, active: false };
        isInitialLoadRef.current = false;
        setIsScrollSwitchSettling(false);
      }
      return;
    }

    if (!userHasScrolledRef.current) {
      hardScrollToBottom();
      isInitialLoadRef.current = false;
      setIsScrollSwitchSettling(false);
    }
  }, [activeConversation?.id, hardScrollToBottom, isLoading, messages]);

  // Keep bottom pinned when late layout shifts change message height (markdown/module mount).
  useEffect(() => {
    const conversationId = activeConversation?.id ?? null;
    const container = messagesContainerRef.current;
    if (!conversationId || !container || typeof ResizeObserver === 'undefined') return;

    const inner = container.querySelector('.chat-messages-inner');
    if (!inner) return;

    let rafId: number | null = null;
    const stickBottomIfNeeded = () => {
      const forceBottom = forceBottomRef.current;
      const shouldForceBottom = forceBottom.active && forceBottom.conversationId === conversationId;
      if (shouldForceBottom || !userHasScrolledRef.current) {
        hardScrollToBottom();
      }
    };

    const observer = new ResizeObserver(() => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      rafId = requestAnimationFrame(() => {
        rafId = null;
        stickBottomIfNeeded();
      });
    });
    observer.observe(inner);

    return () => {
      observer.disconnect();
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [activeConversation?.id, hardScrollToBottom]);

  // Keep scrolled to bottom during streaming (content updates without new array entries)
  useEffect(() => {
    if (!isStreaming) return;
    // User just sent a message — reset scroll lock so they see the response
    userHasScrolledRef.current = false;
    const container = messagesContainerRef.current;
    if (!container) return;

    const interval = setInterval(() => {
      if (!userHasScrolledRef.current) {
        container.scrollTop = container.scrollHeight;
      }
    }, 80);

    return () => clearInterval(interval);
  }, [isStreaming]);

}
